"use client"

import { ApplicationEnvironmentMolecule } from "@/app/env/store"
import {
  createGachaEvmReadiness,
  gachaEvmReadinessSchema,
  getGachaEnvironment,
} from "@anaconda/core/gacha/evm-chains"
import { usePrivy } from "@privy-io/react-auth"
import { useWallets as useSolanaWallets } from "@privy-io/react-auth/solana"
import { wrap } from "@reatom/core"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { useEffect } from "react"
import { getGachaApiUrl } from "./gacha-api"
import {
  gachaNetworkSelection,
  getGachaNetworkStorage,
} from "./gacha-network-selection"
import { useGachaProfileWallet } from "./use-gacha-evm-wallets"

export function useGachaNetworkContext() {
  const options = useMolecule(ApplicationEnvironmentMolecule)
  const expectedEnvironment = getGachaEnvironment(
    options.collectorCryptGachaBaseUrl ?? "",
  )
  const query = useQuery({
    queryKey: ["gacha-evm-chains", options.bffBaseUrl, expectedEnvironment],
    enabled: Boolean(expectedEnvironment),
    retry: false,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: wrap(async ({ signal }: { signal: AbortSignal }) => {
      const response = await wrap(
        fetch(getGachaApiUrl("/evm/chains", options), {
          cache: "no-store",
          signal: AbortSignal.any([signal, AbortSignal.timeout(15_000)]),
        }),
      )
      if (!response.ok) throw new Error("Chain readiness unavailable")
      const current = gachaEvmReadinessSchema.parse(await wrap(response.json()))
      if (current.environment !== expectedEnvironment)
        throw new Error("CollectorCrypt environment mismatch")
      return current
    }),
  })
  const network = gachaNetworkSelection.selected()
  const current =
    !query.isError &&
    !query.isPlaceholderData &&
    query.data?.environment === expectedEnvironment
      ? query.data
      : createGachaEvmReadiness(expectedEnvironment, null)
  const chain = current.chains.find((chain) => chain.chainId === network)
  return {
    selection: gachaNetworkSelection,
    network,
    environment: expectedEnvironment,
    readiness: current,
    chain,
  }
}

export function useGachaNavbarNetwork() {
  const context = useGachaNetworkContext()
  const { ready } = usePrivy()
  const {
    wallet,
    walletAddress: evmAddress,
    ready: evmReady,
  } = useGachaProfileWallet()
  const { ready: solanaReady } = useSolanaWallets()
  // biome-ignore lint/correctness/useExhaustiveDependencies: Reatom wrap hides the callback; these dependencies deliberately refresh its captured context.
  useEffect(
    wrap(() => {
      const activeChain = wallet
        ? Number(wallet.chainId.split(":").at(-1))
        : undefined
      context.selection.initialize(
        context.environment,
        activeChain,
        getGachaNetworkStorage(),
        ready && evmReady && solanaReady,
      )
    }),
    [
      ready,
      evmReady,
      solanaReady,
      context.environment,
      wallet?.chainId,
      context.selection,
    ],
  )
  return { ...context, evmAddress }
}
